<template>
    <v-container fluid>
        <v-card class="elevation-0">
            <v-card-title class="warning white--text">
                <span>Subempreitadas</span>
                <v-spacer />
                <v-btn small color="primary" dark @click="openDialog()">
                    <v-icon left small>mdi-plus</v-icon>
                    Adicionar
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-0">
                <v-simple-table fixed-header>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Trabalho</th>
                                <th>QTD</th>
                                <th>Local</th>
                                <th class="text-right">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in fields.subempreitadas" :key="item.id || index">
                                <td>{{ subcontractText(item) }}</td>
                                <td>{{ valueOrDash(item.qty) }}</td>
                                <td>{{ valueOrDash(item.local) }}</td>
                                <td class="text-right text-no-wrap">
                                    <v-btn icon small color="primary" @click="openDialog(index)">
                                        <v-icon small>mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn icon small color="error" @click="deleteSubempreitada(index)">
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-if="fields.subempreitadas.length === 0">
                                <td colspan="4" class="text-center grey--text py-6">
                                    Sem subempreitadas.
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="dialog" max-width="700" persistent>
            <v-card>
                <v-card-title class="primary white--text">
                    {{ editIndex === null ? 'Adicionar Subempreitada' : 'Editar Subempreitada' }}
                </v-card-title>

                <v-card-text class="pt-6">
                    <v-progress-linear
                        v-if="loadingCatalog"
                        class="mb-5"
                        color="primary"
                        indeterminate
                    />

                    <v-alert v-if="dialogError" type="error" dense text>
                        {{ dialogError }}
                    </v-alert>

                    <v-autocomplete
                        v-model="draft.subempreitada"
                        :items="subempreitadas"
                        :item-text="item => item.Codigo + ' - ' + item.Descricao"
                        item-value="Id"
                        label="Subempreitada *"
                        outlined
                        dense
                    />

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="draft.qty"
                                label="QTD"
                                type="number"
                                step="0.01"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="draft.local"
                                label="Local"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="closeDialog">Cancelar</v-btn>
                    <v-btn color="primary" dark @click="saveLine">Gravar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import SubEmpreitada from '@/api/SubEmpreitada.js'
import dayjs from 'dayjs'

export default {
    name: 'Subcontractors',

    props: {
        fields: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            subempreitadas: [],
            catalogLoaded: false,
            loadingCatalog: false,
            dialog: false,
            editIndex: null,
            draft: {},
            dialogError: null
        }
    },

    methods: {
        async ensureCatalog() {
            if (this.catalogLoaded) return

            this.loadingCatalog = true
            try {
                const response = await SubEmpreitada.all(this.fields.obra_id)
                this.subempreitadas = response.data || []
                this.catalogLoaded = true
            } finally {
                this.loadingCatalog = false
            }
        },

        async openDialog(index = null) {
            this.editIndex = index
            this.dialogError = null

            const source = index === null ? null : this.fields.subempreitadas[index]
            this.draft = source
                ? this.clone(source)
                : {
                    id: `${dayjs().valueOf()}-new`,
                    subempreitada: null,
                    qty: 0,
                    local: null,
                    options: ''
                }

            try {
                await this.ensureCatalog()
                this.dialog = true
            } catch (error) {
                this.dialogError = 'Não foi possível carregar as subempreitadas.'
                this.dialog = true
            }
        },

        closeDialog() {
            this.dialog = false
            this.dialogError = null
        },

        saveLine() {
            this.dialogError = null

            if (!this.draft.subempreitada) {
                this.dialogError = 'Seleciona uma subempreitada.'
                return
            }

            const selected = this.subempreitadas.find(
                item => String(item.Id) === String(this.draft.subempreitada)
            )

            const row = {
                ...this.clone(this.draft),
                codigo: selected ? selected.Codigo : this.draft.codigo,
                descricao: selected ? selected.Descricao : this.draft.descricao
            }

            if (this.editIndex === null) this.fields.subempreitadas.push(row)
            else this.$set(this.fields.subempreitadas, this.editIndex, row)

            this.closeDialog()
        },

        deleteSubempreitada(index) {
            this.fields.subempreitadas.splice(index, 1)
        },

        subcontractText(item) {
            if (!item) return '-'

            const selected = this.subempreitadas.find(
                element => String(element.Id) === String(item.subempreitada)
            )

            if (selected) return `${selected.Codigo} - ${selected.Descricao}`
            if (item.codigo || item.descricao) {
                return `${item.codigo || ''}${item.codigo && item.descricao ? ' - ' : ''}${item.descricao || ''}`
            }

            return this.valueOrDash(item.subempreitada)
        },

        valueOrDash(value) {
            return value === null || value === undefined || value === '' ? '-' : value
        },

        clone(value) {
            return JSON.parse(JSON.stringify(value))
        }
    }
}
</script>
